import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLegal, LEGAL_MODALS } from '../context/LegalContext';
import '../styles/legal.css';

// -----------------------------
// Legal content blocks
// -----------------------------
const AvisoLegalContent = () => (
  <>
    <p>
      En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la
      Sociedad de la Información y Comercio Electrónico (LSSI-CE), se facilita la siguiente
      información general del sitio web:
    </p>
    <ul>
      <li><strong>Titular:</strong> Ideas Costa Blanca</li>
      <li><strong>Teléfono:</strong> +34 604 15 08 71</li>
      <li><strong>Correo electrónico:</strong> info@ideascostablanca.com</li>
    </ul>

    <h3>Objeto</h3>
    <p>
      El presente sitio web tiene por finalidad ofrecer información sobre los servicios de
      construcción, reformas, mantenimiento y climatización prestados por Ideas Costa Blanca,
      así como facilitar el contacto con los usuarios interesados en solicitar presupuesto.
    </p>

    <h3>Condiciones de uso</h3>
    <p>
      El acceso y navegación por este sitio web atribuyen la condición de usuario e implican la
      aceptación de las presentes condiciones. El usuario se compromete a hacer un uso adecuado
      y lícito de los contenidos y servicios del sitio, absteniéndose de utilizarlos para fines
      contrarios a la ley, a la moral o al orden público.
    </p>

    <h3>Propiedad intelectual e industrial</h3>
    <p>
      Todos los contenidos del sitio web (textos, imágenes, diseño, código fuente, logotipos y
      demás elementos) son propiedad de Ideas Costa Blanca o de terceros que han autorizado su
      uso. Queda prohibida la reproducción, distribución, comunicación pública o transformación,
      total o parcial, sin autorización expresa del titular.
    </p>

    <h3>Limitación de responsabilidad</h3>
    <p>
      Ideas Costa Blanca no será responsable de los daños o perjuicios que pudieran derivarse de
      interferencias, omisiones, interrupciones, virus informáticos, averías telefónicas o
      desconexiones en el funcionamiento operativo del sistema electrónico, motivadas por causas
      ajenas a su control. Los presupuestos y datos técnicos indicados en este sitio son
      orientativos y deberán confirmarse mediante visita y presupuesto detallado.
    </p>

    <h3>Legislación aplicable</h3>
    <p>
      Las presentes condiciones se rigen por la legislación española. Para la resolución de
      cualquier controversia, las partes se someten a los juzgados y tribunales del domicilio
      del usuario cuando este actúe como consumidor.
    </p>
  </>
);

const PoliticaPrivacidadContent = () => (
  <>
    <p>
      De conformidad con el Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018 de
      Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD), le
      informamos sobre el tratamiento de sus datos personales.
    </p>

    <h3>Responsable del tratamiento</h3>
    <ul>
      <li><strong>Responsable:</strong> Ideas Costa Blanca</li>
      <li><strong>Correo electrónico:</strong> info@ideascostablanca.com</li>
      <li><strong>Teléfono:</strong> +34 604 15 08 71</li>
    </ul>

    <h3>Datos que recogemos</h3>
    <p>A través del formulario de contacto y solicitud de presupuesto recogemos únicamente:</p>
    <ul>
      <li>Nombre</li>
      <li>Teléfono</li>
      <li>Correo electrónico (opcional)</li>
      <li>Servicio de interés (opcional)</li>
      <li>Mensaje o descripción de la consulta</li>
    </ul>

    <h3>Finalidad y base legitimadora</h3>
    <p>
      Los datos se tratan con la <strong>única finalidad de gestionar y responder a su consulta
      o solicitud de presupuesto</strong>. No se utilizarán para ningún otro fin. La base
      legitimadora del tratamiento es el consentimiento del interesado que se otorga marcando
      la casilla correspondiente en el formulario.
    </p>

    <h3>Cesión de datos y marketing</h3>
    <p>
      <strong>No cedemos sus datos a terceros.</strong> No los utilizamos con fines publicitarios
      ni de marketing directo salvo consentimiento expreso adicional otorgado por usted.
    </p>

    <h3>Plazo de conservación</h3>
    <p>
      Sus datos se conservarán durante el tiempo necesario para gestionar la consulta o el
      presupuesto solicitado y, posteriormente, durante el plazo estrictamente necesario para
      atender posibles responsabilidades legales derivadas del servicio prestado. Transcurrido
      ese plazo, los datos serán suprimidos.
    </p>

    <h3>Sus derechos</h3>
    <p>Puede ejercer en cualquier momento sus derechos de:</p>
    <ul>
      <li>Acceso a sus datos personales</li>
      <li>Rectificación de datos inexactos</li>
      <li>Supresión (derecho al olvido)</li>
      <li>Oposición al tratamiento</li>
      <li>Limitación del tratamiento</li>
      <li>Portabilidad de sus datos</li>
      <li>Retirar el consentimiento otorgado</li>
    </ul>
    <p>
      Para ejercer estos derechos puede enviar un correo electrónico a{' '}
      <a href="mailto:info@ideascostablanca.com">info@ideascostablanca.com</a> indicando el
      derecho que desea ejercer. Asimismo, tiene derecho a presentar una reclamación ante la
      Agencia Española de Protección de Datos (www.aepd.es) si considera que el tratamiento no
      se ajusta a la normativa.
    </p>
  </>
);

const PoliticaCookiesContent = () => (
  <>
    <h3>¿Qué son las cookies?</h3>
    <p>
      Una cookie es un pequeño archivo de texto que un sitio web guarda en su navegador para
      permitir su funcionamiento correcto, mejorar la experiencia de navegación o, en algunos
      casos, obtener información estadística.
    </p>

    <h3>Cookies utilizadas en este sitio</h3>
    <p><strong>Cookies técnicas o necesarias (exentas de consentimiento).</strong> Son
    imprescindibles para el correcto funcionamiento del sitio y no requieren el consentimiento
    del usuario según la LSSI-CE:</p>
    <ul>
      <li>
        <strong>icb_cookie_consent_v1</strong> (localStorage) — Guarda su elección respecto al
        uso de cookies para no volver a mostrar el banner. Duración: hasta que sea eliminada
        manualmente.
      </li>
    </ul>

    <p><strong>Cookies de análisis o de terceros (requieren consentimiento).</strong> Este
    sitio no utiliza actualmente cookies de análisis, publicidad ni de terceros. Si en el
    futuro se incorporasen, se cargarían únicamente tras su consentimiento a través del banner
    y esta política se actualizaría con el detalle de cada una (nombre, finalidad, duración y
    proveedor).</p>

    <h3>Cómo gestionar o desactivar las cookies</h3>
    <p>
      Puede modificar su elección en cualquier momento pulsando el enlace{' '}
      <em>«Configurar cookies»</em> disponible en el pie de página. Adicionalmente, la mayoría
      de navegadores permite bloquear o eliminar cookies desde su configuración:
    </p>
    <ul>
      <li>Google Chrome: Configuración → Privacidad y seguridad → Cookies</li>
      <li>Mozilla Firefox: Ajustes → Privacidad y seguridad</li>
      <li>Safari: Preferencias → Privacidad</li>
      <li>Microsoft Edge: Configuración → Cookies y permisos del sitio</li>
    </ul>
    <p>
      Le informamos de que la desactivación de cookies técnicas puede provocar el
      funcionamiento incorrecto de determinadas partes del sitio.
    </p>
  </>
);

const CONTENT_MAP = {
  [LEGAL_MODALS.AVISO]: { title: 'Aviso Legal', body: <AvisoLegalContent /> },
  [LEGAL_MODALS.PRIVACIDAD]: { title: 'Política de Privacidad', body: <PoliticaPrivacidadContent /> },
  [LEGAL_MODALS.COOKIES]: { title: 'Política de Cookies', body: <PoliticaCookiesContent /> },
};

// -----------------------------
// Modal component
// -----------------------------
const LegalModal = () => {
  const { openModal, closeLegalModal } = useLegal();
  const active = openModal ? CONTENT_MAP[openModal] : null;

  // Escape key to close + lock body scroll while open
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLegalModal();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [active, closeLegalModal]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          className="legal-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={closeLegalModal}
          data-testid="legal-modal-backdrop"
          role="presentation"
        >
          <motion.div
            className="legal-modal"
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="legal-modal-title"
            data-testid={`legal-modal-${openModal}`}
          >
            <div className="legal-modal-header">
              <h2 id="legal-modal-title" className="legal-modal-title">{active.title}</h2>
              <button
                type="button"
                className="legal-modal-close"
                onClick={closeLegalModal}
                aria-label="Cerrar"
                data-testid="legal-modal-close"
              >
                <X size={22} strokeWidth={2} />
              </button>
            </div>
            <div className="legal-modal-body">{active.body}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LegalModal;
